/* @jsx h */
import { defineComponent, h, ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { setTitle } from "@lemmonhq/orders";
import {
  DateRangePicker,
  type DateRange,
  type DateRangeOption,
  getDateRange,
  useLocalization,
} from "@lemmonhq/uikit";
import { OverviewTab } from "./tabs/OverviewTab";
import { RevenueTab } from "./tabs/RevenueTab";
import { ProductsTab } from "./tabs/ProductsTab";
import { CategoriesTab } from "./tabs/CategoriesTab";
import { SettlementsTab } from "./tabs/SettlementsTab";

type TabKey =
  | "overview"
  | "revenue"
  | "products"
  | "categories"
  | "settlements";

const TABS: { key: TabKey; icon: string; labelKey: string }[] = [
  {
    key: "overview",
    icon: "account_tree",
    labelKey: "reports.coa.tab_overview",
  },
  { key: "revenue", icon: "monitoring", labelKey: "reports.coa.tab_revenue" },
  {
    key: "products",
    icon: "inventory_2",
    labelKey: "reports.coa.tab_products",
  },
  {
    key: "categories",
    icon: "category",
    labelKey: "reports.coa.tab_categories",
  },
  {
    key: "settlements",
    icon: "account_balance",
    labelKey: "reports.coa.tab_settlements",
  },
];

export const ChartOfAccountsPage = defineComponent({
  name: "ChartOfAccountsPage",
  setup() {
    const { t } = useLocalization();
    const route = useRoute();
    const router = useRouter();

    onMounted(() => setTitle(t("reports.coa.title")));

    const dateRangeOptions = computed<DateRangeOption[]>(() => [
      { value: "today", label: t("date_range_picker.options.today") },
      { value: "this_week", label: t("date_range_picker.options.this_week") },
      { value: "this_month", label: t("date_range_picker.options.this_month") },
      {
        value: "this_quarter",
        label: t("date_range_picker.options.this_quarter"),
      },
      { value: "this_year", label: t("date_range_picker.options.this_year") },
      {
        value: "last_12m",
        label: t("date_range_picker.options.last_12_months"),
      },
      { value: "custom", label: t("date_range_picker.options.custom_range") },
    ]);

    const resolveInitialRange = (): DateRange => {
      const q = route.query;
      const type =
        typeof q.dateRange === "string" ? (q.dateRange as any) : "this_month";
      if (
        type === "custom" &&
        typeof q.start === "string" &&
        typeof q.end === "string"
      ) {
        return { type: "custom", startDate: q.start, endDate: q.end };
      }
      const r = getDateRange(type);
      return { type, startDate: r.startDate, endDate: r.endDate };
    };

    const selectedRange = ref<DateRange>(resolveInitialRange());
    const fromIso = computed(
      () => `${selectedRange.value.startDate}T00:00:00.000Z`,
    );
    const toIso = computed(
      () => `${selectedRange.value.endDate}T23:59:59.999Z`,
    );

    const activeTab = ref<TabKey>(
      TABS.some((tb) => tb.key === route.query.tab)
        ? (route.query.tab as TabKey)
        : "overview",
    );
    // Cross-tab drill-down: a category selected in Revenue filters Products.
    const productCategory = ref<number | null>(null);

    const syncQuery = () => {
      router.replace({
        query: {
          ...route.query,
          tab: activeTab.value,
          dateRange: selectedRange.value.type,
          start:
            selectedRange.value.type === "custom"
              ? selectedRange.value.startDate
              : undefined,
          end:
            selectedRange.value.type === "custom"
              ? selectedRange.value.endDate
              : undefined,
        },
      });
    };

    const setTab = (tab: TabKey) => {
      activeTab.value = tab;
      syncQuery();
    };

    const handleRangeChange = (range: DateRange | undefined) => {
      if (!range) return;
      selectedRange.value = range;
      syncQuery();
    };

    const handleSelectCategory = (categoryId: number) => {
      productCategory.value = categoryId;
      setTab("products");
    };

    const renderTabContent = () => {
      if (activeTab.value === "overview") {
        return (
          <div class="h-full overflow-y-auto px-4 sm:px-6 py-4">
            <div class="mx-auto">
              <OverviewTab from={fromIso.value} to={toIso.value} />
            </div>
          </div>
        );
      }
      if (activeTab.value === "revenue") {
        return (
          <div class="h-full overflow-y-auto px-4 sm:px-6 py-4">
            <div class="mx-auto">
              <RevenueTab
                from={fromIso.value}
                to={toIso.value}
                onSelectCategory={handleSelectCategory}
              />
            </div>
          </div>
        );
      }
      if (activeTab.value === "products") {
        return (
          <div class="h-full px-4 sm:px-6 py-4 flex flex-col min-h-0">
            <div class="mx-auto w-full flex flex-col min-h-0 flex-1">
              <ProductsTab
                from={fromIso.value}
                to={toIso.value}
                categoryId={productCategory.value}
                onCategoryChange={(id) => (productCategory.value = id)}
              />
            </div>
          </div>
        );
      }
      if (activeTab.value === "settlements") {
        return (
          <div class="h-full overflow-y-auto px-4 sm:px-6 py-4">
            <div class="mx-auto">
              <SettlementsTab from={fromIso.value} to={toIso.value} />
            </div>
          </div>
        );
      }
      return (
        <div class="h-full px-4 sm:px-6 py-4 flex flex-col min-h-0">
          <div class="mx-auto w-full flex flex-col min-h-0 flex-1">
            <CategoriesTab from={fromIso.value} to={toIso.value} />
          </div>
        </div>
      );
    };

    return () => (
      <div class="h-full flex flex-col flex-1 overflow-hidden">
        {/* Header */}
        <div class="px-4 sm:px-6 pt-4 pb-3 border-b border-base-300/50 flex flex-col gap-3 bg-base-200">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 class="text-xl font-semibold text-accent flex items-center gap-2">
                <span class="material-symbols-outlined text-primary">
                  account_tree
                </span>
                {t("reports.coa.title")}
              </h1>
              <p class="text-sm text-base-content/55">
                {t("reports.coa.subtitle")}
              </p>
            </div>
            <div class="w-full sm:w-auto sm:min-w-[280px] flex justify-end">
              <DateRangePicker
                options={dateRangeOptions.value}
                value={selectedRange.value}
                onChange={handleRangeChange}
              />
            </div>
          </div>

          {/* Tabs — one row that scrolls sideways on phones instead of
              squeezing the labels under their icons. */}
          <div
            role="tablist"
            class="tabs tabs-boxed bg-base-neutral self-start !flex max-w-full flex-nowrap overflow-x-auto"
          >
            {TABS.map((tb) => (
              <a
                key={tb.key}
                role="tab"
                class={`tab shrink-0 gap-1.5 whitespace-nowrap ${
                  activeTab.value === tb.key ? "tab-active" : ""
                }`}
                onClick={() => setTab(tb.key)}
              >
                <span class="material-symbols-outlined text-base">
                  {tb.icon}
                </span>
                {t(tb.labelKey)}
              </a>
            ))}
          </div>
        </div>

        {/* Body */}
        <div class="flex-1 min-h-0 overflow-hidden">{renderTabContent()}</div>
      </div>
    );
  },
});
